/**
 * Who was there — resolved down the trip → segment → activity chain.
 *
 * Nobody wants to re-pick the same four people on every activity. A segment
 * ("the cruise", "Day 3 — Nassau") can name the group that was on it, and
 * anything under it inherits that group until it says otherwise:
 *
 *   activity's own list  →  nearest ancestor activity with a list
 *                        →  nearest ancestor segment with a list  →  whole party
 *
 * Activities nest too (`parentInteractionId`), and they inherit the same way:
 * if only half the party went into the park, everything logged under the park
 * check-in follows that half without being told twice.
 *
 * Inheritance is resolved on read rather than copied at creation time, so
 * correcting a segment's group also corrects every activity beneath it that
 * never disagreed. An empty list means "not set" at every level, matching how
 * activities have always treated a missing `participants` field.
 */

import { activityAncestors } from "./activityTree";

interface SegmentLike {
  id: string;
  parentSegmentId?: string | null;
  participants?: string[] | null;
}

interface ActivityLike {
  id: string;
  segmentId?: string | null;
  parentInteractionId?: string | null;
  participants?: string[] | null;
}

export type SegmentParticipantsLookup = (segmentId: string | null | undefined) => string[];

/**
 * Keep only ids that are still in the travel party, in party order — a member
 * removed from the trip shouldn't linger in a segment's saved list.
 */
function pruneToParty(ids: string[] | null | undefined, allMemberIds: string[]): string[] {
  if (!Array.isArray(ids) || ids.length === 0) return [];
  const named = new Set(ids);
  return allMemberIds.filter((id) => named.has(id));
}

/**
 * Builds a memoized lookup of the party that was on a segment, walking up the
 * parent chain to the first segment that names one and falling back to the
 * whole travel party.
 */
export function segmentParticipantsLookup(
  segments: SegmentLike[],
  allMemberIds: string[],
): SegmentParticipantsLookup {
  const byId = new Map(segments.map((s) => [s.id, s]));
  const resolved = new Map<string, string[]>();
  return (segmentId) => {
    if (!segmentId) return allMemberIds;
    const cached = resolved.get(segmentId);
    if (cached) return cached;
    let cur = byId.get(segmentId);
    const seen = new Set<string>();
    let party = allMemberIds;
    while (cur && !seen.has(cur.id)) {
      seen.add(cur.id);
      const named = pruneToParty(cur.participants, allMemberIds);
      if (named.length > 0) {
        party = named;
        break;
      }
      cur = cur.parentSegmentId ? byId.get(cur.parentSegmentId) : undefined;
    }
    resolved.set(segmentId, party);
    return party;
  };
}

/**
 * Who was at one activity: its own list when it has one, otherwise the group
 * inherited from its segment. Use `inheritedActivityParticipants` where the
 * activity tree is on hand — it consults the places an activity sits inside
 * before falling back to the segment.
 */
export function resolveActivityParticipants(
  activity: { segmentId?: string | null; participants?: string[] | null },
  lookup: SegmentParticipantsLookup,
  allMemberIds: string[],
): string[] {
  const own = pruneToParty(activity.participants, allMemberIds);
  if (own.length > 0) return own;
  return lookup(activity.segmentId);
}

export interface InheritedParticipants {
  /** The group to fall back on, resolved down the chain. */
  participants: string[];
  /**
   * The activity it came from, when an enclosing activity named it — so the UI
   * can say "Following Magic Kingdom" rather than naming the segment. Null when
   * the group came from a segment (or is just the whole party).
   */
  sourceActivityId: string | null;
}

/**
 * What an activity inherits when it names no group of its own: the nearest
 * activity it sits inside that names one, and only then the segment chain.
 *
 * Nearer wins. Someone stayed back at the hotel for the park day, so the park
 * check-in names the four who went; the rides under it follow the check-in, not
 * the segment's six. Resolved on read like the rest of the chain, so correcting
 * the check-in corrects everything under it that never disagreed.
 */
export function inheritedActivityParticipants(
  activity: ActivityLike,
  activities: ActivityLike[],
  segmentLookup: SegmentParticipantsLookup,
  allMemberIds: string[],
): InheritedParticipants {
  for (const ancestor of activityAncestors(activities, activity.id)) {
    const named = pruneToParty(ancestor.participants, allMemberIds);
    if (named.length > 0) {
      return { participants: named, sourceActivityId: ancestor.id };
    }
  }
  return { participants: segmentLookup(activity.segmentId), sourceActivityId: null };
}
